/**
 * Hyreskontrakt för studentbostad (Formulär R-14), IR twin of the legacy
 * `studentbostad/v1` template. Same shape as bostadshyra: auto-numbered
 * `§` sections, a custom-sections anchor after each, the static clauses
 * unnumbered ahead of the signatures.
 */
import { applyDefaults, COMPOSITION_SCHEMA, COMPOSITION_VERSION } from '@hyranai/regta-document-ir';
import { optionOf } from '../bostadshyra/kit.js';
import { declaredSectionInstances } from '../common/custom-sections.js';
import { whenOption } from '../common/slots.js';
import { STUDENTBOSTAD_BLOCKS } from '../studentbostad/blocks.js';
import { RENT_MODEL_OPTIONS } from '../studentbostad/options.js';
const TITLE = 'Hyreskontrakt för studentbostad';
const FORM_NUMBER = 'Formulär R-14';
const PREAMBLE = 'Lägenheten hyrs ut för att användas till studentbostad. Mellan hyresvärd och hyresgäst har följande avtal träffats.';
const STATIC_CLAUSES = [
    { instanceId: 'rokforbud', blockRef: 'studentbostad.rokforbud@v1', input: {} },
    { instanceId: 'hyresgastens-ataganden', blockRef: 'studentbostad.hyresgastens-ataganden@v1', input: {} },
    { instanceId: 'force-majeure', blockRef: 'studentbostad.force-majeure@v1', input: {} },
    { instanceId: 'tidigare-hyresavtal', blockRef: 'studentbostad.tidigare-hyresavtal@v1', input: {} },
];
const SECTIONS = [
    { ref: 'hyresvard', blockRef: 'studentbostad.hyresvard@v1' },
    { ref: 'hyresgast', blockRef: 'studentbostad.hyresgast@v1', besidePrevious: true },
    { ref: 'lagenhets-adress', blockRef: 'studentbostad.lagenhets-adress@v1' },
    { ref: 'anvandning', blockRef: 'studentbostad.anvandning@v1' },
    { ref: 'storlek', blockRef: 'studentbostad.storlek@v1' },
    { ref: 'tillbehor', blockRef: 'studentbostad.tillbehor@v1' },
    { ref: 'hyrestid', blockRef: 'studentbostad.hyrestid@v1' },
    { ref: 'uppsagning', blockRef: 'studentbostad.uppsagning@v1' },
    { ref: 'hyressattningsmodell', blockRef: 'studentbostad.hyressattningsmodell@v1' },
    {
        ref: 'forhandlingsklausul',
        blockRef: 'studentbostad.forhandlingsklausul@v1',
        includeWhen: whenOption('rent_model', optionOf(RENT_MODEL_OPTIONS, 'kollektiva')),
    },
    { ref: 'hyra', blockRef: 'studentbostad.hyra@v1' },
    { ref: 'tillagg', blockRef: 'studentbostad.tillagg@v1' },
    { ref: 'korridorstadning', blockRef: 'studentbostad.korridorstadning@v1' },
    { ref: 'betalning', blockRef: 'studentbostad.betalning@v1' },
    { ref: 'deposition', blockRef: 'studentbostad.deposition@v1' },
    { ref: 'garage', blockRef: 'studentbostad.garage@v1' },
    { ref: 'reservnycklar', blockRef: 'studentbostad.reservnycklar@v1' },
    { ref: 'sarskilda-bestammelser', blockRef: 'studentbostad.sarskilda-bestammelser@v1' },
    {
        ref: 'underskrift',
        blockRef: 'studentbostad.underskrifter@v1',
        before: STATIC_CLAUSES,
        input: {
            statement: true,
            parties: [
                { label: 'Hyresvärden', role: 'lessor', ordinal: 0 },
                { label: 'Hyresgästen', role: 'lessee', ordinal: 0 },
            ],
        },
    },
];
/** The legacy create-time defaults, read off the slots' own `default` (option codes for selects). */
export const STUDENTBOSTAD_DEFAULTS = applyDefaults({
    fields: STUDENTBOSTAD_BLOCKS.flatMap((block) => block.slots),
});
export const studentbostadV1 = (values = {}) => ({
    schema: COMPOSITION_SCHEMA,
    version: COMPOSITION_VERSION,
    documentKey: 'studentbostad',
    documentVersion: 'v1',
    title: TITLE,
    locale: 'sv-SE',
    meta: {
        category: 'Studentbostad',
        numberStyle: 'paragraph',
        shortTitle: 'Studentbostadsavtal',
        formNumber: FORM_NUMBER,
        preamble: PREAMBLE,
        description: 'Bostadshyresavtal för studentbostad — lägenheten är avsedd för person som bedriver aktiva universitets-, högskole- eller därmed jämförliga studier.\n\n' +
            'Avtalet stödjer både enkelrum i korridorboende (med gemensamma utrymmen) och hela lägenheter. Med hänsyn till normalstudietid är hyresförhållandet avsett att upphöra efter maximalt cirka fem år, men kan förlängas på hyresgästens skriftliga begäran om aktiva studier fortgår eller om synnerliga skäl föreligger.\n\n' +
            'Vem undertecknar: hyresvärden och hyresgästen via Scrive.\n\n' +
            'För vanliga bostadshyresavtal — använd istället mallen Bostadshyresavtal.',
        signingMode: 'digital',
        partySlots: [
            { id: 'lessor', label: 'Hyresvärd', role: 'lessor', ordinal: 0, required: true, signs: true },
            { id: 'lessee', label: 'Hyresgäst', role: 'lessee', ordinal: 0, required: true, signs: true },
        ],
        propertySlots: [{ id: 'property', label: 'Fastighet', multi: false, required: true }],
    },
    values: { ...STUDENTBOSTAD_DEFAULTS, ...values },
    blocks: [
        { instanceId: 'letterhead', blockRef: 'common.letterhead@v1', slot: 'letterhead', input: { title: TITLE, form_number: FORM_NUMBER } },
        { instanceId: 'footer', blockRef: 'common.page-footer@v1', slot: 'footer', input: { form_number: FORM_NUMBER } },
        { instanceId: 'avtalsuppgifter', blockRef: 'studentbostad.avtalsuppgifter@v1', input: {} },
        { instanceId: 'preamble', blockRef: 'common.preamble@v1', input: { text: PREAMBLE, variant: 'intro' } },
        ...declaredSectionInstances(SECTIONS, { customBlockRef: 'studentbostad.custom-sections@v1' }),
    ],
});
//# sourceMappingURL=studentbostad.js.map